import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { supabase, type ClothingItem, type JewelryItem, type SavedLook } from "@/lib/supabase";
import { AppShell } from "@/components/app-shell";
import { ImageBox, deleteStoredPhoto } from "@/components/ui-extras";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FavoriteHeart, ItemActions } from "@/components/item-actions";
import { PhotoViewer } from "@/components/photo-viewer";
import { ClothingEditDialog } from "@/components/clothing-edit-dialog";
import {
  DetailsCard,
  NotesCard,
  EyeVerdictCard,
  FeelingsSection,
  StyleThisPiece,
  CompleteTheLook,
  SuggestedSearches,
  SavedLooksGrid,
} from "@/components/item-detail-sections";
import {
  matchJewelryForClothing,
  missingCategories,
  suggestedSearchesForClothing,
} from "@/lib/style-matching";
import { JEWELRY_CATEGORY_PRESETS } from "@/components/smart-select";
import { useCollectionTraversal } from "@/lib/use-collection-traversal";
import { ItemSwipeShell } from "@/components/item-swipe-shell";
import { BackgroundPreferenceSelector } from "@/components/bg-preference-selector";

function WoreThisToday({ itemId, itemType }: { itemId: string; itemType: "jewelry" | "clothing" | "accessory" }) {
  const { user } = useAuth();
  const [logged, setLogged] = useState(false);
  const [fading, setFading] = useState(false);

  async function handleLog() {
    if (logged || !user) return;
    setLogged(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      await supabase.from("wear_logs").insert({
        user_id: user.id,
        item_id: itemId,
        item_type: itemType,
        worn_at: today,
        logged_via: "manual",
      } as never);
    } catch {
      // Silent
    }
    setTimeout(() => {
      setFading(true);
      setTimeout(() => { setLogged(false); setFading(false); }, 600);
    }, 2000);
  }

  return (
    <button
      type="button"
      onClick={handleLog}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        marginTop: 20,
        padding: "var(--caileve-space-2) var(--caileve-space-5)",
        borderRadius: "var(--caileve-radius-pill)",
        border: logged ? "1px solid rgba(198,166,107,0.5)" : "1px solid rgba(198,166,107,0.25)",
        backgroundColor: logged ? "rgba(198,166,107,0.06)" : "transparent",
        cursor: logged ? "default" : "pointer",
        transition: "all 200ms ease",
        opacity: fading ? 0 : 1,
      }}
    >
      <svg
        width="13" height="13" viewBox="0 0 24 24"
        fill={logged ? "var(--caileve-gold)" : "none"}
        stroke={logged ? "var(--caileve-gold)" : "var(--caileve-muted)"}
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ transition: "all 200ms ease", flexShrink: 0 }}
      >
        <circle cx="12" cy="12" r="10" />
      </svg>
      <span style={{
        fontFamily: "Inter, sans-serif",
        fontSize: 12,
        letterSpacing: "0.15em",
        textTransform: "uppercase",
        color: logged ? "var(--caileve-gold)" : "var(--caileve-muted)",
        transition: "color 200ms ease",
      }}>
        {logged ? "Noted" : "Wore this today"}
      </span>
    </button>
  );
}

export const Route = createFileRoute("/clothing/$id")({
  component: () => (
    <AppShell>
      <ClothingDetail />
    </AppShell>
  ),
});

function ClothingDetail() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  if (!user) return null;
  const uid = user.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const traversal = useCollectionTraversal(id, ["clothing_items", uid]);
  const navigateToItem = useCallback(
    (targetId: string) => {
      setViewerOpen(false);
      navigate({ to: "/clothing/$id", params: { id: targetId }, replace: true });
    },
    [navigate],
  );
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);

  const { data: item, isLoading } = useQuery({
    queryKey: ["clothing_item", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clothing_items")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw new Error(error.message || "Network error");
      return (data as ClothingItem | null) ?? null;
    },
  });

  const { data: jewelry = [] } = useQuery({
    queryKey: ["jewelry_items_for_clothing", uid],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jewelry_items")
        .select("*")
        .eq("user_id", uid)
        .eq("is_accessory", false);
      if (error) throw new Error(error.message || "Network error");
      return (data as JewelryItem[] | null) ?? [];
    },
    enabled: !!uid,
  });

  const { data: looksData = { looks: [], feelings: [] } } = useQuery({
    queryKey: ["item_saved_looks", "clothing", id],
    queryFn: async () => {
      const { data: links, error: linkErr } = await supabase
        .from("saved_look_items")
        .select("look_id")
        .or(`clothing_item_id.eq.${id},and(item_type.eq.clothing,item_id.eq.${id})`);
      if (linkErr) throw linkErr;
      const ids = Array.from(new Set((links ?? []).map((l) => (l as { look_id: string }).look_id)));
      if (ids.length === 0) return { looks: [] as SavedLook[], feelings: [] as string[] };
      const { data: looks, error } = await supabase
        .from("saved_looks")
        .select("*")
        .in("id", ids)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      const list = (looks as (SavedLook & { feelings?: string | null })[] | null) ?? [];
      const feelingSet = new Set<string>();
      list.forEach((l) => {
        const raw = (l as { feelings?: string | null }).feelings;
        if (raw) raw.split(",").map((s) => s.trim()).filter(Boolean).forEach((f) => feelingSet.add(f));
      });
      return { looks: list, feelings: Array.from(feelingSet) };
    },
  });

  const toggleFav = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const { error } = await supabase
        .from("clothing_items")
        .update({ is_favorite: !item.is_favorite } as never)
        .eq("id", item.id);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clothing_item", id] });
      qc.invalidateQueries({ queryKey: ["clothing_items", uid] });
      qc.invalidateQueries({ queryKey: ["clothing_favorites", uid] });
    },
  });


  const del = useMutation({
    mutationFn: async () => {
      const { error: linkErr } = await supabase
        .from("saved_look_items")
        .delete()
        .eq("clothing_item_id", id);
      if (linkErr) throw linkErr;
      const { error } = await supabase.from("clothing_items").delete().eq("id", id);
      if (error) throw new Error(error.message || "Network error");
      await deleteStoredPhoto(item?.photo_url, { table: "clothing_items", id });
    },
    onSuccess: () => {
      toast.success("Garment removed");
      qc.invalidateQueries({ queryKey: ["clothing_items", uid] });
      qc.invalidateQueries({ queryKey: ["clothing_favorites", uid] });
      navigate({ to: "/clothing", search: { new: undefined } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-12">
        <div className="animate-pulse space-y-4">
          <div className="h-3 w-20 rounded-full" style={{ backgroundColor: "var(--caileve-soft)" }} />
          <div className="w-full aspect-[4/5] rounded-sm" style={{ backgroundColor: "var(--caileve-soft)" }} />
          <div className="h-7 w-52 rounded-full" style={{ backgroundColor: "var(--caileve-soft)" }} />
          <div className="h-3 w-28 rounded-full" style={{ backgroundColor: "var(--caileve-soft)" }} />
          <div className="pt-4 space-y-3">
            <div className="h-3 w-full rounded-full" style={{ backgroundColor: "var(--caileve-soft)" }} />
            <div className="h-3 w-4/5 rounded-full" style={{ backgroundColor: "var(--caileve-soft)" }} />
            <div className="h-3 w-3/5 rounded-full" style={{ backgroundColor: "var(--caileve-soft)" }} />
          </div>
        </div>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="font-serif-display text-3xl">Garment not found.</p>
        <Button variant="ghost" className="mt-6" onClick={() => navigate({ to: "/clothing", search: { new: undefined } })}>
          Back to Clothing
        </Button>
      </div>
    );
  }

  const { matches, rationale } = matchJewelryForClothing(item, jewelry, 4);

  const ownedCategories = new Set(
    jewelry.map((j) => (j.category ?? "").toLowerCase()).filter(Boolean),
  );
  const missing = missingCategories(ownedCategories, JEWELRY_CATEGORY_PRESETS, 3);

  const searches = suggestedSearchesForClothing(item);

  return (
    <ItemSwipeShell
      key={id}
      prevItem={traversal.prevItem}
      nextItem={traversal.nextItem}
      navigateTo={navigateToItem}
      collectionLabel="Clothing"
    >
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-64">
      <div className="flex items-center justify-between">
        <Link
          to="/clothing"
          search={{ new: undefined }}
          className="inline-flex items-center text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3 mr-1" /> Clothing
        </Link>
        <ItemActions
          onEdit={() => setEditOpen(true)}
          onDelete={() => setConfirmDelete(true)}
        />
      </div>

      {/* Hero photo — tap opens viewer, but only when the touch
          didn't travel horizontally far enough to be a swipe. */}
      <button
        type="button"
        onClick={() => setViewerOpen(true)}
        onTouchStart={(e) => {
          (e.currentTarget as HTMLButtonElement).dataset.touchX =
            String(e.touches[0].clientX);
        }}
        onTouchEnd={(e) => {
          const startX = parseFloat(
            (e.currentTarget as HTMLButtonElement).dataset.touchX ?? "0"
          );
          const dx = Math.abs(e.changedTouches[0].clientX - startX);
          if (dx > 10) e.preventDefault();
        }}
        className="block w-full mt-4 cursor-zoom-in"
        aria-label="View photo full screen"
      >
        {(() => {
          const noBgUrl = (item as { photo_url_no_bg?: string | null }).photo_url_no_bg;
          const preferClean = !!(item as { prefer_clean_bg?: boolean }).prefer_clean_bg && !!noBgUrl;
          const c = (item as { clean_bg_color?: string | null }).clean_bg_color ?? "lilac";
          const heroBg = c === "ivory" ? "#F5EEE1" : c === "white" ? "#FFFFFF" : "var(--caileve-soft)";
          return (
            <ImageBox
              src={preferClean ? noBgUrl! : item.photo_url}
              alt={item.name}
              aspect="aspect-[4/5]"
              style={preferClean ? { backgroundColor: heroBg } : undefined}
            />
          );
        })()}
      </button>

      <BackgroundPreferenceSelector
        item={item as never}
        table="clothing_items"
        itemQueryKey={["clothing_item", id]}
        itemsQueryKey={["clothing_items", uid]}
        uid={uid}
      />

      <div className="mt-5 flex items-start justify-between gap-4">
        <h1
          className="font-serif-display leading-tight"
          style={{ color: "var(--caileve-ink)", fontSize: 28 }}
        >
          {item.name}
        </h1>
        <FavoriteHeart
          size="detail"
          active={!!item.is_favorite}
          onToggle={() => toggleFav.mutate()}
        />
      </div>

      <FeelingsSection
        feelings={looksData.feelings}
        userId={uid}
        itemId={item.id}
        kind="clothing"
        itemFeelings={(item as unknown as { feelings?: string[] | null }).feelings ?? []}
      />

      <DetailsCard
        rows={[
          { label: "Clothing Type", value: item.clothing_type },
          {
            label: "Color",
            value: [
              item.primary_color,
              (item as unknown as { secondary_color?: string | null }).secondary_color,
            ]
              .filter(Boolean)
              .join(" · ") || null,
          },
          { label: "Pattern", value: item.pattern },
          { label: "Neckline", value: item.neckline },
          { label: "Storage Location", value: item.storage_location },
          { label: "Purchase Price", value: item.price != null ? `$${item.price}` : null },
          { label: "Occasion", value: item.occasion },
        ]}
      />

      <NotesCard notes={item.notes} />
      <EyeVerdictCard verdict={item.eye_verdict} />
      <WoreThisToday itemId={item.id} itemType="clothing" />

      <StyleThisPiece
        items={matches}
        rationale={rationale}
        kind="jewelry"
        sourceItem={{
          id: item.id,
          type: "clothing",
          name: item.name,
          photo_url: item.photo_url,
        }}
      />

      <CompleteTheLook
        categories={missing}
        context={{
          sourceKind: "clothing",
          sourceCategory: item.clothing_type,
          color: item.primary_color,
          pattern: item.pattern,
        }}
      />

      <SuggestedSearches queries={searches} />

      <SavedLooksGrid looks={looksData.looks} />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <ClothingEditDialog item={item} userId={uid} onDone={() => setEditOpen(false)} />
      </Dialog>

      <PhotoViewer
        open={viewerOpen}
        src={item.photo_url}
        alt={item.name}
        onClose={() => setViewerOpen(false)}
        onEdit={() => {
          setViewerOpen(false);
          setEditOpen(true);
        }}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this garment?</AlertDialogTitle>
            <AlertDialogDescription>
              "{item.name}" will be permanently removed from your wardrobe. Looks that include it
              will keep their other pieces.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={del.isPending}
              onClick={(e) => {
                e.preventDefault();
                del.mutate();
              }}
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </ItemSwipeShell>
  );
}